import { Link } from 'react-router';
import { FileText } from 'lucide-react';
import type { Artifact } from '@/lib/api';
import { StatusPill, type StatusPillStatus } from '@/components/ui/status-pill';

interface ArtifactsTableProps {
  artifacts: Artifact[];
}

const STATUS_MAP: Record<string, StatusPillStatus> = {
  active: 'active',
  draft: 'draft',
  published: 'published',
  archived: 'archived',
};

export function ArtifactsTable({ artifacts }: ArtifactsTableProps): React.ReactElement {
  if (artifacts.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface px-4 py-8 text-center text-sm text-text-tertiary">
        No artifacts yet.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-inset text-left">
            {['Title', 'Type', 'Scope', 'Status', 'Owner', 'Updated'].map(h => (
              <th
                key={h}
                className="px-3.5 py-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {artifacts.map((a, i) => {
            const isAgent = (a.ownerLogin ?? a.ownerId ?? '').toLowerCase() === 'agent';
            return (
              <tr
                key={a.id}
                className={
                  i < artifacts.length - 1
                    ? 'border-b border-border/60 hover:bg-surface-elevated'
                    : 'hover:bg-surface-elevated'
                }
              >
                <td className="px-3.5 py-2.5">
                  <Link
                    to={`/artifacts/${encodeURIComponent(a.id)}`}
                    className="inline-flex items-center gap-2 text-text-primary hover:text-accent-bright"
                  >
                    <FileText className="size-3.5 shrink-0 text-accent-bright" />
                    <span className="truncate font-medium">{a.title}</span>
                  </Link>
                </td>
                <td className="px-3.5 py-2.5">
                  <span className="rounded border border-border bg-surface-elevated px-1.5 py-0.5 font-mono text-[10px] tracking-wide text-text-secondary">
                    {a.type}
                  </span>
                </td>
                <td className="px-3.5 py-2.5 font-mono text-[11.5px] text-text-secondary">
                  {a.scope}
                </td>
                <td className="px-3.5 py-2.5">
                  {STATUS_MAP[a.status] ? (
                    <StatusPill status={STATUS_MAP[a.status]} />
                  ) : (
                    <span className="font-mono text-[11.5px] text-text-tertiary">{a.status}</span>
                  )}
                </td>
                <td
                  className={`px-3.5 py-2.5 font-mono text-[11.5px] ${
                    isAgent ? 'text-accent-bright' : 'text-text-tertiary'
                  }`}
                >
                  {a.ownerLogin ?? a.ownerId ?? '—'}
                </td>
                <td className="px-3.5 py-2.5 font-mono text-[11px] text-text-tertiary">
                  {a.updatedAt.slice(0, 19).replace('T', ' ')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
